<template>
    <div class="page-customized">
        <v-progress-linear
            v-if="overlay"
            absolute
            indeterminate
        ></v-progress-linear>

        <app-bar
            :title="athlete.exists() ? t('club_athlete_title_update') : t('club_athlete_title_create')"
            back="/club-athletes"
        />

        <v-layout class="mt-n8">
            <v-card
                width="100%"
                color="transparent"
                elevation="0"
                class="pb-8"
            >
                <v-card-text class="pb-0">
                    <v-form class="mt-6">
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('club_athlete_label_name') }}</div>
                            <div class="horizontal-input">
                                <v-text-field
                                    v-model="athlete.name"
                                    type="text"
                                    required
                                    outlined
                                    dense
                                    :error-messages="form.errors.get('name')"
                                ></v-text-field>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('club_athlete_label_title') }}</div>
                            <div class="horizontal-input">
                                <v-text-field
                                    v-model="athlete.title"
                                    type="text"
                                    outlined
                                    dense
                                    :error-messages="form.errors.get('title')"
                                ></v-text-field>
                            </div>
                            <div class="horizontal-actions">
                                <edit-translations-modal
                                    v-model="athlete.title"
                                    :items="athlete.translations['title']"
                                    @touch="(v) => athlete.translations['title'] = v"
                                ></edit-translations-modal>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('club_athlete_label_photo') }}</div>
                            <div class="horizontal-input">
                                <v-file-input
                                    v-model="athlete.photo"
                                    outlined
                                    prepend-icon=""
                                    prepend-inner-icon="mdi-paperclip"
                                    :append-outer-icon="athlete.photoUrl ? 'mdi-eye-outline' : ''"
                                    dense
                                    :error-messages="photoForm.errors.get('photo')"
                                    @click:append-outer="viewPhoto()"
                                    @click:clear="athlete.photo = undefined"
                                ></v-file-input>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('club_athlete_label_link_text') }}</div>
                            <div class="horizontal-input">
                                <v-text-field
                                    v-model="athlete.linkText"
                                    type="text"
                                    outlined
                                    dense
                                    :error-messages="form.errors.get('link_text')"
                                ></v-text-field>
                            </div>
                            <div class="horizontal-actions">
                                <edit-translations-modal
                                    v-model="athlete.linkText"
                                    :items="athlete.translations['link_text']"
                                    @touch="(v) => athlete.translations['link_text'] = v"
                                ></edit-translations-modal>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('club_product_label_link_mode') }}</div>
                            <div class="horizontal-input pb-6">
                                <v-btn-toggle
                                    v-model="linkMode"
                                    active-class="primary"
                                    class="my-1"
                                    dense
                                    mandatory
                                >
                                    <v-btn
                                        value="external"
                                        class="exs mr-3"
                                        small
                                    >{{ t('club_product_label_link_external') }}</v-btn>
                                    <v-btn
                                        value="dynamic"
                                        class="exs"
                                        small
                                    >{{ t('club_product_label_link_dynamic') }}</v-btn>
                                </v-btn-toggle>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t(linkMode === 'external' ? 'club_product_label_link_url' : 'club_athlete_label_link_params') }}</div>
                            <div class="horizontal-input">
                                <v-text-field
                                    v-if="linkMode === 'external'"
                                    v-model="athlete.link"
                                    type="text"
                                    outlined
                                    dense
                                    :error-messages="form.errors.get('link')"
                                ></v-text-field>
                                <template v-else>
                                    <div
                                        v-for="(row, i) in linkParamRows"
                                        :key="`param_${i}`"
                                        class="d-flex mb-2"
                                    >
                                        <v-text-field
                                            v-model="row.key"
                                            :placeholder="t('club_product_label_param_key')"
                                            outlined
                                            dense
                                            hide-details
                                            class="mr-2"
                                        ></v-text-field>
                                        <v-text-field
                                            v-model="row.value"
                                            :placeholder="t('club_product_label_param_value')"
                                            outlined
                                            dense
                                            hide-details
                                            class="mr-2"
                                        ></v-text-field>
                                        <v-btn
                                            @click="linkParamRows.splice(i, 1)"
                                            icon
                                            class="mt-1"
                                        ><v-icon>mdi-close</v-icon></v-btn>
                                    </div>
                                    <v-btn
                                        @click="linkParamRows.push({ key: '', value: '' })"
                                        v-ripple="false"
                                        plain
                                        color="orange darken-4"
                                        class="add mb-4 ml-1"
                                    ><v-icon left size="16">mdi-plus-circle</v-icon>{{ t('club_product_action_add_param') }}</v-btn>
                                </template>
                            </div>
                        </div>
                        <div class="form-group-horizontal-cols">
                            <div class="form-group-horizontal">
                                <div class="horizontal-label">{{ t('club_athlete_label_gold_count') }}</div>
                                <div class="horizontal-input">
                                    <v-text-field
                                        v-model="athlete.goldCount"
                                        type="number"
                                        step="1"
                                        min="0"
                                        outlined
                                        dense
                                        :error-messages="form.errors.get('gold_count')"
                                    ></v-text-field>
                                </div>
                            </div>
                            <div class="form-group-horizontal">
                                <div class="horizontal-label">{{ t('club_athlete_label_silver_count') }}</div>
                                <div class="horizontal-input">
                                    <v-text-field
                                        v-model="athlete.silverCount"
                                        type="number"
                                        step="1"
                                        min="0"
                                        outlined
                                        dense
                                        :error-messages="form.errors.get('silver_count')"
                                    ></v-text-field>
                                </div>
                            </div>
                            <div class="form-group-horizontal">
                                <div class="horizontal-label">{{ t('club_athlete_label_bronze_count') }}</div>
                                <div class="horizontal-input">
                                    <v-text-field
                                        v-model="athlete.bronzeCount"
                                        type="number"
                                        step="1"
                                        min="0"
                                        outlined
                                        dense
                                        :error-messages="form.errors.get('bronze_count')"
                                    ></v-text-field>
                                </div>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('club_athlete_label_status') }}</div>
                            <div class="horizontal-input">
                                <v-select
                                    v-model="athlete.status"
                                    :items="clubAthleteStatuses()"
                                    outlined
                                    append-icon="mdi-chevron-down"
                                    dense
                                    :error-messages="form.errors.get('status')"
                                ></v-select>
                            </div>
                        </div>
                    </v-form>
                </v-card-text>

                <v-card-actions class="px-4">
                    <v-btn
                        :to="{ name: 'club-athletes' }"
                        class="mr-4"
                        color="primary"
                    >{{ t('general_action_cancel') }}</v-btn>
                    <v-btn
                        :loading="form.busy"
                        :disabled="form.busy"
                        color="orange"
                        @click.prevent="submit"
                    >{{ athlete.exists() ? t('general_action_save') : t('club_athlete_title_create') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-layout>
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent, onMounted, reactive, toRefs, watch } from '@vue/composition-api'
    import { useI18n } from 'vue-i18n-composable'
    import { useStore } from '@/store'
    import { useRouter } from '@/composition-api'
    import { useForm } from '@/core/form'
    import { useEnums } from '@/core/defaults'
    import { ClubAthlete } from '@/core/models/club-athlete'
    import EditTranslationsModal from '@/components/EditTranslationsModal.vue'

    export default defineComponent({
        components: {
            EditTranslationsModal,
        },

        setup(props) {
            const store = useStore()
            const router = useRouter()
            const i18n = useI18n()
            const id = router.currentRoute.params['id'] || undefined

            const state = reactive({
                overlay: true,
                linkParamRows: <{ key: string, value: string }[]>[],
                linkMode: 'external',
            })

            const athleteModel = computed(() => <ClubAthlete>store.getters['clubAthlete/get'])

            const form = reactive(useForm())
            const photoForm = reactive(useForm())

            watch(() => state.linkMode, (mode) => {
                if (mode === 'dynamic' && ! state.linkParamRows.length) {
                    state.linkParamRows.push({ key: '', value: '' })
                }
            })

            const capture = () => {
                store.dispatch('clubAthlete/getOrNew', { id: id, payload: { append: 'translations' } }).then(() => {
                    state.linkParamRows = Object.entries(athleteModel.value.linkParams || {}).map(([key, value]) => ({ key, value: <string>value }))
                    state.linkMode = state.linkParamRows.length > 0 ? 'dynamic' : 'external'
                    state.overlay = false
                })
            }

            const submit = () => {
                athleteModel.value.linkParams = state.linkMode === 'dynamic'
                    ? state.linkParamRows.filter((row) => row.key).reduce((acc: any, row) => { acc[row.key] = row.value; return acc }, {})
                    : {}

                form.usingSubmit(store.dispatch('clubAthlete/updateOrCreate', {
                    id: id,
                    payload: athleteModel.value.payload()
                }))
                    .then(() => {
                        const savedId = <any>athleteModel.value.id
                        const next = () => router.push({ name: 'club-athletes.view', params: <any>{ id: savedId } })
                        store.dispatch('notify', { color: 'success', message: i18n.t('general_notice_data_updated') })
                        if (athleteModel.value.photo) {
                            const data = new FormData()
                            data.append('photo', athleteModel.value.photo)
                            photoForm.usingSubmit(store.dispatch('clubAthlete/photo', { id: savedId, payload: data }).then(() => {
                                next()
                            }))
                        } else {
                            next()
                        }
                    })
            }

            const viewPhoto = () => {
                window.open(athleteModel.value.photoUrl)
            }

            onMounted(() => {
                capture()
            })

            return {
                form,
                photoForm,
                capture,
                viewPhoto,
                submit,
                athlete: athleteModel.value,
                ...useI18n(),
                ...useEnums(),
                ...toRefs(state)
            }
        }
    })
</script>
